import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ConfigOptions, ConfigService } from '@wilson/config';
import { ShiftAttachment, ShiftAttachmentType } from '@wilson/interfaces';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

export interface UploadedAttachment {
  createdAt: string;
  fileName: string;
  id: string;
  ownerId: string;
  presignedUrl: string;
  preSignedPreviewUrl?: string;
  shiftId: string;
  sizeInBytes: number;
}

@Injectable({
  providedIn: 'root',
})
export class ShiftAttachmentsService {
  protected readonly http = inject(HttpClient);
  protected readonly config = inject<ConfigOptions>(ConfigService);

  protected readonly path = 'shifts';

  addShiftAttachments(
    shiftId: string,
    files: File[] | NzUploadFile[],
  ): Observable<UploadedAttachment[]> {
    if (files.length) {
      return forkJoin(
        files.map((file) => {
          const formData = new FormData();
          formData.append('file', file as unknown as File);

          return this.http
            .post<UploadedAttachment>(
              `${this.config.host}/${this.path}/${shiftId}/attachments`,
              formData,
            )
            .pipe(catchError(() => of(null)));
        }),
      ).pipe(
        map((results) => {
          if (results.some((result) => result === null)) {
            throw new Error('One or more attachments failed to upload');
          }
          return results as UploadedAttachment[];
        }),
      );
    } else {
      return of([]);
    }
  }

  addShiftReportAttachments(
    shiftId: string,
    files: File[] | NzUploadFile[],
  ): Observable<UploadedAttachment[]> {
    if (files.length) {
      return forkJoin(
        files.map((file) => {
          const formData = new FormData();
          formData.append('file', file as unknown as File);
          formData.append('attachmentType', ShiftAttachmentType.Report);

          return this.http
            .post<UploadedAttachment>(
              `${this.config.host}/${this.path}/${shiftId}/attachments`,
              formData,
            )
            .pipe(catchError(() => of(null)));
        }),
      ).pipe(
        map((results) => {
          if (results.some((result) => result === null)) {
            throw new Error('One or more attachments failed to upload');
          }
          return results as UploadedAttachment[];
        }),
      );
    } else {
      return of([]);
    }
  }

  deleteShiftAttachments(
    shiftId: string,
    fileId: string,
  ): Observable<UploadedAttachment> {
    return this.http.delete<UploadedAttachment>(
      `${this.config.host}/${this.path}/${shiftId}/attachments/${fileId}`,
    );
  }

  deleteShiftReportAttachments(
    shiftId: string,
    fileId: string,
  ): Observable<unknown> {
    const attachmentType = ShiftAttachmentType.Report;
    return this.http.delete(
      `${this.config.host}/${this.path}/${shiftId}/attachments/${fileId}?attachmentType=${attachmentType}`,
    );
  }

  getShiftAttachments(shiftId: string): Observable<UploadedAttachment[]> {
    return this.http.get<UploadedAttachment[]>(
      `${this.config.host}/${this.path}/${shiftId}/attachments`,
    );
  }

  getShiftReportAttachments(shiftId: string): Observable<UploadedAttachment[]> {
    const attachmentType = ShiftAttachmentType.Report;
    return this.http.get<UploadedAttachment[]>(
      `${this.config.host}/${this.path}/${shiftId}/attachments?attachmentType=${attachmentType}`,
    );
  }

  getShiftAttachmentsByType(
    shiftId: string,
    type: ShiftAttachmentType,
  ): Observable<UploadedAttachment[]> {
    return this.http.get<UploadedAttachment[]>(
      `${this.config.host}/${this.path}/${shiftId}/attachments?attachmentType=${type}`,
    );
  }

  getBulkReportAttachmentsByShiftIds(
    shiftIds: string[],
  ): Observable<Record<string, ShiftAttachment[]>> {
    return this.http.post<Record<string, ShiftAttachment[]>>(
      `${this.config.host}/${this.path}/attachments/bulk`,
      {
        shiftIds,
        type: ShiftAttachmentType.Report,
      },
    );
  }

  getBulkOtherAttachmentsByShiftIds(
    shiftIds: string[],
  ): Observable<Record<string, ShiftAttachment[]>> {
    return this.http.post<Record<string, ShiftAttachment[]>>(
      `${this.config.host}/${this.path}/attachments/bulk`,
      {
        shiftIds,
        type: ShiftAttachmentType.Other,
      },
    );
  }

  getAllAttachmentsByShiftIds(
    shiftIds: string[],
  ): Observable<Record<string, ShiftAttachment[]>> {
    return this.http.post<Record<string, ShiftAttachment[]>>(
      `${this.config.host}/${this.path}/attachments/bulk`,
      {
        shiftIds,
      },
    );
  }

  exportShiftsReportPdf({
    shiftIds,
    clientId,
  }: {
    shiftIds: string[];
    clientId: string | null;
  }): Observable<Blob> {
    return this.http.post(
      `${this.config.host}/${this.path}/attachments/report-pdf`,
      {
        shiftIds,
        clientId,
      },
      {
        responseType: 'blob',
      },
    );
  }

  uploadFileToMultipleShifts(
    file: File | NzUploadFile,
    attachmentType: ShiftAttachmentType,
    shiftIds: string[],
  ): Observable<ShiftAttachment[]> {
    const formData = new FormData();
    formData.append('file', file as unknown as File);
    formData.append('attachmentType', attachmentType);
    shiftIds.forEach((shiftId) => {
      formData.append('shiftIds[]', shiftId);
    });

    return this.http.post<ShiftAttachment[]>(
      `${this.config.host}/${this.path}/attachments/upload-to-multiple`,
      formData,
    );
  }
}
